/**
 * CompanySelector - Multi-Company Support
 * Dropdown in header for switching between companies
 */

import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Check, RefreshCw } from 'lucide-react';
import api from '@/services/api';

const CompanySelector = ({ 
  userId = 'default', 
  theme = 'light',
  onCompanyChange = null
}) => {
  const isDark = theme === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadCurrentCompany();
  }, [userId]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/report-engine/companies');
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      // Set default company
      setCompanies([{ id: 'C001', name: 'Default Company' }]);
    }
  };

  const loadCurrentCompany = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/report-engine/company/current/${userId}`);
      if (response.data.success) {
        const companyId = response.data.data.companyId;
        setCurrentCompany(companyId);
      }
    } catch (error) {
      console.error('Error loading current company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (companyId) => {
    if (companyId === currentCompany) {
      setIsOpen(false);
      return;
    }

    setSwitching(true);
    try {
      await api.post('/api/report-engine/company/switch', { userId, companyId });
      setCurrentCompany(companyId);
      onCompanyChange?.(companyId);
      setIsOpen(false);
    } catch (error) {
      console.error('Error switching company:', error);
    } finally {
      setSwitching(false);
    }
  };

  const currentCompanyData = companies.find(c => c.id === currentCompany) || companies[0];

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading || switching}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors justify-between ${
          isDark 
            ? 'bg-white/5 text-white hover:bg-white/10' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${(loading || switching) ? 'opacity-70' : ''}`}
        data-testid="company-selector-btn"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Building2 size={16} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
          <span className="truncate">
            {loading ? 'Loading...' : currentCompanyData?.name || 'Select Company'}
          </span>
        </span>
        {switching ? (
          <RefreshCw size={14} className="animate-spin shrink-0" />
        ) : (
          <ChevronDown size={14} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div 
            className={`absolute right-0 top-full mt-2 w-full rounded-xl border shadow-2xl z-50 overflow-hidden ${
              isDark ? 'bg-[#151515] border-[#333]' : 'bg-white border-gray-200'
            }`}
            data-testid="company-selector-dropdown"
          >
            {/* Header */}
            <div className={`px-4 py-3 border-b ${isDark ? 'border-[#333]' : 'border-gray-100'}`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Switch Company
              </h3>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                Select a company to view its data
              </p>
            </div>

            {/* Company List */}
            <div className="max-h-64 overflow-y-auto py-2">
              {companies.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Building2 className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                    No companies available
                  </p>
                </div>
              ) : (
                companies.map((company) => {
                  const isSelected = company.id === currentCompany;
                  return (
                    <button
                      key={company.id}
                      onClick={() => handleSelectCompany(company.id)}
                      disabled={switching}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                        isSelected
                          ? isDark ? 'bg-blue-500/10' : 'bg-blue-50'
                          : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`company-${company.id}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected
                          ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                          : isDark ? 'bg-white/5 text-slate-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Building2 size={18} />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          isSelected
                            ? isDark ? 'text-blue-400' : 'text-blue-600'
                            : isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {company.name}
                        </p>
                        {company.city && (
                          <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                            {company.city}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <Check size={16} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {currentCompanyData && (
              <div className={`px-4 py-2 border-t ${isDark ? 'border-[#333] bg-[#0e0e10]' : 'border-gray-100 bg-gray-50'}`}>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Current: <span className="font-medium">{currentCompanyData.name}</span>
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySelector;
